// The gate between one DME order stage and the next.
//
// Asks for exactly what dmeWorkflow.requirementsFor says the next stage needs,
// uploads any document straight into the client's Drive folder under DME, and
// only then moves the order — with an audit row. Pages open this instead of
// advancing an order themselves, so a stage can no longer be "clicked" past.
import { useEffect, useMemo, useState } from 'react';
import { fileName } from '../../lib/documents/fileNames';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { uploadAndLinkDoc } from '../../lib/driveDocs';
import {
  nextStatus, statusInfo, requirementsFor, advanceOrder, loadFeeSchedule, feeItemFor, money,
} from '../../lib/dmeWorkflow';

const inputStyle = { width: '100%', padding: '8px 10px', fontSize: 12, borderRadius: 7, border: '0.5px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontFamily: 'inherit', boxSizing: 'border-box' };
const labelStyle = { fontSize: 11, fontWeight: 500, color: 'var(--text2)', marginBottom: 4, display: 'block' };
const hintStyle  = { fontSize: 10.5, color: 'var(--text3)', marginTop: 3 };

// `target` is the next stage unless the page names one — a denial is a step
// sideways from Claim Submitted, not the step after it.
export default function AdvanceOrderModal({ order, target: wanted, onClose, onAdvanced }) {
  const { profile } = useAuth();
  const target = wanted || nextStatus(order.status);
  const [ctx, setCtx] = useState({ client: null, feeItem: null, suppliers: [], cmns: [], ready: false });
  const [draft, setDraft] = useState({});
  const [uploading, setUploading] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [{ data: client }, schedule, { data: suppliers }, { data: cmns }] = await Promise.all([
        supabase.from('clients').select('id, client_id, first_name, last_name, date_of_birth, drive_folder_id, service_type').eq('id', order.client_id).single(),
        loadFeeSchedule(),
        supabase.from('dme_suppliers').select('id, name').eq('is_active', true).order('name'),
        supabase.from('dme_cmns').select('id, status, document_url, hcpcs_codes, signed_date, expiry_date').eq('client_id', order.client_id).order('created_at', { ascending: false }),
      ]);
      if (cancelled) return;
      setCtx({ client, feeItem: feeItemFor(schedule, order.hcpcs_code), suppliers: suppliers || [], cmns: cmns || [], ready: true });
    })();
    return () => { cancelled = true; };
  }, [order.client_id, order.hcpcs_code]);

  // Evaluate the gate against the order as it would be after this draft.
  const merged = useMemo(() => ({ ...order, ...draft }), [order, draft]);
  const req = useMemo(() => (target ? requirementsFor(target, merged, ctx) : null), [target, merged, ctx]);

  // Seed defaults the first time the requirements are known.
  useEffect(() => {
    if (!req || !ctx.ready) return;
    setDraft(d => {
      const seeded = { ...d };
      for (const f of req.fields) if (seeded[f.key] === undefined && f.value !== '' && f.value !== null && f.value !== undefined) seeded[f.key] = f.value;
      return seeded;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.ready, target]);

  const set = (key) => (e) => setDraft(d => ({ ...d, [key]: e.target.value }));

  const handleFile = (field) => async (e) => {
    const file = e.target.files?.[0];
    if (!file || !ctx.client) return;
    setUploading(field.key); setError('');
    try {
      const stamp = new Date().toISOString().slice(0, 10);
      const filename = fileName([ctx.client.client_id, order.order_ref || 'DME', field.docType, stamp], (file.name.match(/\.([a-z0-9]+)$/i)?.[1] || 'pdf'));
      const url = await uploadAndLinkDoc({
        blob: file, filename, contentType: file.type,
        clientCode: ctx.client.client_id, clientUuid: ctx.client.id,
        documentType: field.docType, subfolder: 'DME',
        notes: `Order ${order.order_ref}`, profile, existingFolderId: ctx.client.drive_folder_id,
      });
      setDraft(d => ({ ...d, [field.key]: url }));
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(null);
    }
  };

  const confirm = async () => {
    if (!req?.canAdvance) return;
    setSaving(true); setError('');
    const patch = {};
    for (const f of req.fields) if (draft[f.key] !== undefined && draft[f.key] !== '') patch[f.key] = draft[f.key];
    if (patch.supplier_id) patch.supplier_name = ctx.suppliers.find(s => s.id === patch.supplier_id)?.name || null;
    if (target === 'cmn_complete' && ctx.feeItem) patch.cmn_required = !!ctx.feeItem.cmn_required;
    if (target === 'auth_pending' && ctx.feeItem && order.quantity_ordered) {
      patch.expected_amount = Math.round(Number(ctx.feeItem.rate) * Number(order.quantity_ordered) * 100) / 100;
    }
    for (const k of ['frequency_per_day', 'quantity_delivered', 'claim_amount', 'paid_amount']) if (patch[k] !== undefined) patch[k] = Number(patch[k]);
    const { data, error: err } = await advanceOrder({ order, target, patch, profile, note: req.note || null });
    setSaving(false);
    if (err) { setError(err.message); return; }
    onAdvanced?.(data);
    onClose?.();
  };

  if (!target) return null;
  const to = statusInfo(target);

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--card-bg, var(--bg))', borderRadius: 12, width: 'min(560px, 100%)', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ padding: '16px 22px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{order.order_ref} · {order.client_name}</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginTop: 2 }}>
              {statusInfo(order.status).label} <span style={{ color: 'var(--text3)', fontWeight: 400 }}>→</span> <span style={{ color: to.color }}>{to.label}</span>
            </div>
          </div>
          <button onClick={onClose} className="btn btn-outline btn-sm">Cancel</button>
        </div>

        <div style={{ padding: '16px 22px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {!ctx.ready && <div style={{ fontSize: 12.5, color: 'var(--text3)' }}>Checking what this stage needs…</div>}

          {ctx.ready && req.blocking.length > 0 && (
            <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger)', borderRadius: 8, padding: '10px 12px', fontSize: 12.5, color: 'var(--danger)' }}>
              <b>Can't move to {to.label} yet</b>
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>{req.blocking.map((b, i) => <li key={i}>{b}</li>)}</ul>
            </div>
          )}
          {ctx.ready && req.note && (
            <div style={{ background: 'var(--warn-bg)', border: '1px solid var(--warn-border, var(--warn))', borderRadius: 8, padding: '10px 12px', fontSize: 12.5, color: 'var(--warn)' }}>{req.note}</div>
          )}
          {ctx.ready && req.fields.length === 0 && req.blocking.length === 0 && (
            <div style={{ fontSize: 12.5, color: 'var(--text2)' }}>Nothing further is needed for this step.</div>
          )}

          {ctx.ready && req.fields.map(f => (
            <div key={f.key}>
              <label style={labelStyle}>{f.label}{f.required && <span style={{ color: 'var(--danger)' }}> *</span>}</label>
              {f.type === 'text' && <input value={draft[f.key] ?? ''} onChange={set(f.key)} style={inputStyle} />}
              {f.type === 'number' && <input type="number" min="0" step="any" value={draft[f.key] ?? ''} onChange={set(f.key)} style={inputStyle} />}
              {f.type === 'date' && <input type="date" value={draft[f.key] ?? ''} onChange={set(f.key)} style={inputStyle} />}
              {f.type === 'select' && (
                <select value={draft[f.key] ?? ''} onChange={set(f.key)} style={inputStyle}>
                  {f.options.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
              )}
              {f.type === 'supplier' && (
                <select value={draft[f.key] ?? ''} onChange={set(f.key)} style={inputStyle}>
                  <option value="">— Select supplier —</option>
                  {f.options.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              )}
              {f.type === 'cmn' && (
                <select value={draft[f.key] ?? ''} onChange={set(f.key)} style={inputStyle}>
                  <option value="">— Select a signed CMN for this client —</option>
                  {f.options.map(c => (
                    <option key={c.id} value={c.id}>
                      {(c.hcpcs_codes || []).join(', ') || 'CMN'} · {c.status}{c.signed_date ? ` · signed ${c.signed_date}` : ''}{c.document_url ? '' : ' · no document'}
                    </option>
                  ))}
                </select>
              )}
              {f.type === 'file' && (
                draft[f.key]
                  ? <div style={{ fontSize: 12, display: 'flex', gap: 10, alignItems: 'center' }}>
                      <a href={draft[f.key]} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>Filed in Drive → G{ctx.client?.client_id?.replace(/^G/, '')}/DME</a>
                      <button type="button" className="btn btn-outline btn-sm" style={{ fontSize: 10 }} onClick={() => setDraft(d => ({ ...d, [f.key]: '' }))}>Replace</button>
                    </div>
                  : <input type="file" accept="application/pdf,image/*" disabled={uploading === f.key} onChange={handleFile(f)} style={{ fontSize: 12 }} />
              )}
              {uploading === f.key && <div style={hintStyle}>Uploading to Drive…</div>}
              {f.hint && uploading !== f.key && <div style={hintStyle}>{f.hint}</div>}
            </div>
          ))}

          {target === 'claim_submitted' && order.expected_amount && (
            <div style={{ fontSize: 12, color: 'var(--text2)' }}>Fee schedule amount for this order: <b>{money(order.expected_amount)}</b></div>
          )}
          {error && <div style={{ fontSize: 12, color: 'var(--danger)' }}>{error}</div>}
        </div>

        <div style={{ padding: '12px 22px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" disabled={!ctx.ready || !req?.canAdvance || saving || !!uploading} onClick={confirm}
            style={{ opacity: (!ctx.ready || !req?.canAdvance || saving) ? 0.5 : 1 }}>
            {saving ? 'Saving…' : `Move to ${to.label}`}
          </button>
        </div>
      </div>
    </div>
  );
}
